import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withState, withHandlers, onlyUpdateForKeys, withProps } from 'recompose';
import { connect } from 'react-redux';
import { DeviceIndex, DeviceId, DeviceMap } from 'types';
import { ReportDevice } from 'logic/reports/ReportDevice';
import { State } from 'reducers';
import { isLoadingDevices, getDevices } from 'selectors/deviceSelectors';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { SelectedCameraList } from './SelectedCameraList';
import { AddCamerasDialog } from './AddCamerasDialog';
import { Button } from 'ui/shared/Button';
import { Required } from 'ui/pages/designer/Required';

export type OwnProps = {
    isExit?: boolean;
    allowMultiple: boolean; // HeatMap can only have one camera. All others can have multiple
    settings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawCamerasSection = ({
    isExit = false,
    allowMultiple,
    dialogVisible,
    selectedCameras,
    selectedCameraIds,
    hiddenIds = [],
    handleClickAddCameras,
    handleRemoveCamera,
    handleAccept,
    handleCancel,
    ...props
}: OwnProps & RecomposeProps) =>
    <Fragment>
        <SelectedCameraList
            idModifier={ isExit ? 'exit' : '' }
            selected={ selectedCameras }
            onRemove={ handleRemoveCamera }
        />
        <div className="inline-child-divs">
            <Button
                id={ `edit-report-add-${isExit ? 'exit-' : ''}cameras-button` }
                type="button"
                onClick={ handleClickAddCameras }
                textKey={
                    allowMultiple
                        ? 'SERVER_CONFIG_BTN_ADD_CAMERA_plural'
                        : selectedCameraIds.length
                            ? '6.3234.1703.1'
                            : 'SERVER_CONFIG_BTN_ADD_CAMERA'
                }
            />
            <Required visible={ selectedCameras.length === 0 }/>
        </div>
        <AddCamerasDialog
            visible={ dialogVisible }
            selected={ selectedCameraIds }
            hiddenIds={ hiddenIds }
            onAccept={ handleAccept }
            onCancel={ handleCancel }
            multiselect={ allowMultiple }
        />
    </Fragment>
;

type RecomposeProps = {
    dialogVisible: boolean;
    handleClickAddCameras: () => void;
    handleRemoveCamera: (deviceIndex: DeviceIndex) => void;
    handleAccept: (selectedCameras: ReadonlyArray<DeviceId>) => void;
    handleCancel: () => void;
    selectedCameras: ReadonlyArray<ReportDevice>;
    selectedCameraIds: ReadonlyArray<DeviceId>;
    hiddenIds: ReadonlyArray<DeviceId>;
};
type _RecomposeProps = RecomposeProps & {
    _setDialogVisible: (visible: boolean) => void;
    _setSelectedCameras: (d: ReadonlyArray<ReportDevice>) => void;
};

type StateProps = {
    allDevices: DeviceMap;
    loading: boolean;
};

const noDevices = {};

const mapStateToProps = (state: State) => ({
    allDevices: getDevices(state) || noDevices,
    loading: isLoadingDevices(state),
});

// Take an array of ReportDevices (just device index and server ID) and a map of all devices
// and return an array of device IDs that correspond to the ReportDevices.
// We do this because all the new code (v7+) works on Guids and all the old code (v6-) works on ints...
// and all the reporting backend is still v6.
const deviceIdsFromReportDevices = (reportDevices: ReadonlyArray<ReportDevice>, allDevices: Readonly<DeviceMap>): DeviceId[] =>
    reportDevices.map(({ deviceIndex }) =>
        Object.keys(allDevices).filter(deviceId =>
            allDevices[deviceId].deviceIndex === deviceIndex
        )
        .shift()!
    )
    .filter(deviceId => !!deviceId)
;

export const CamerasSection = compose<OwnProps & _RecomposeProps, OwnProps>(
    setDisplayName('CamerasSection'),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withProps(({ isExit, settings, allDevices }: OwnProps & StateProps) => {
        const selectedCameras =
            isExit
                ? settings.exitDevices
                : settings.devices;

        const selectedCameraIds = deviceIdsFromReportDevices(selectedCameras, allDevices);

        // AIR-35112: don't show entrance cameras on the exit cameras screen (and vice versa)
        const notAllowedCameras =
            isExit
                ? settings.devices
                : settings.exitDevices;

        const hiddenIds = deviceIdsFromReportDevices(notAllowedCameras, allDevices);

        return {
            selectedCameras,
            selectedCameraIds,
            hiddenIds,
        };
    }),
    withState('dialogVisible', '_setDialogVisible', false),
    withHandlers<OwnProps & StateProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleClickAddCameras: ({ _setDialogVisible }) => () => {
            _setDialogVisible(true);
        },
        handleRemoveCamera: ({ isExit, selectedCameras, onChange }) => (deviceIndex: DeviceIndex) => {
            const cameras = selectedCameras.filter(c =>
                c.deviceIndex !== deviceIndex
            );
            onChange(reportSettings => isExit ? reportSettings.setExitDevices(cameras) : reportSettings.setDevices(cameras));
        },
        handleAccept: ({ isExit, allDevices, onChange, _setDialogVisible }) => (selectedCameras: ReadonlyArray<DeviceId>) => {
            // Convert from DeviceId[] back into ReportDevice[]
            const cameras = selectedCameras.map(deviceId => {
                const camera = allDevices[deviceId];
                return ReportDevice.fromCamera(camera);
            });
            onChange(reportSettings => isExit ? reportSettings.setExitDevices(cameras) : reportSettings.setDevices(cameras));
            _setDialogVisible(false);
        },
        handleCancel: ({ _setDialogVisible }) => () => {
            _setDialogVisible(false);
        },
    }),
    onlyUpdateForKeys([ 'dialogVisible', 'allDevices', 'selectedCameras' ]),
)(RawCamerasSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/cameras/CamerasSection.tsx